import { CommonModule } from '@angular/common';
import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxChange, MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { take } from 'rxjs';

import { ConfirmComponent } from '../../../../../components/core/confirm/confirm.component';
import * as TodoActions from '../../../../../store/todo/todo.actions';
import type { TodoItem } from '../../../../../store/todo/todo.models';
import { selectTodoItems, selectTodoLoading, selectTodoSaving } from '../../../../../store/todo/todo.selectors';

@Component({
  selector: 'app-todo-screen',
  standalone: true,
  imports: [
    CommonModule,
    DragDropModule,
    MatButtonModule,
    MatCardModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
  ],
  templateUrl: './todo-screen.component.html',
  styleUrl: './todo-screen.component.scss',
})
export class TodoScreenComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly actions$ = inject(Actions);
  private readonly dialog = inject(MatDialog);
  private readonly store = inject(Store);

  protected readonly draft = signal('');
  protected readonly editingTodoId = signal<string | null>(null);
  protected readonly editingDraft = signal('');
  protected readonly loading = toSignal(this.store.select(selectTodoLoading), { initialValue: false });
  protected readonly saving = toSignal(this.store.select(selectTodoSaving), { initialValue: false });
  protected readonly todos = signal<TodoItem[]>([]);
  protected readonly pendingTodos = computed(() => this.todos().filter((todo) => !todo.is_completed));
  protected readonly completedTodos = computed(() => this.todos().filter((todo) => todo.is_completed));
  protected readonly summary = computed(() => ({
    total: this.todos().length,
    pending: this.pendingTodos().length,
    completed: this.completedTodos().length,
  }));
  protected readonly canAddTodo = computed(() => !!this.draft().trim() && !this.saving());

  constructor() {
    this.store.select(selectTodoItems)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((items) => {
        this.todos.set(items ?? []);
      });

    this.actions$.pipe(
      ofType(TodoActions.TodoCreateSuccess),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => {
      this.draft.set('');
    });

    this.store.dispatch(TodoActions.TodoItemsFetchRequest());
  }

  protected addTodo(): void {
    const title = this.draft().trim();
    if (!title || this.saving()) {
      return;
    }

    this.store.dispatch(TodoActions.TodoCreateRequest({
      payload: { title },
    }));
  }

  protected toggleTodo(todo: TodoItem, event: MatCheckboxChange): void {
    if (this.isEditing(todo.id)) {
      return;
    }

    this.store.dispatch(TodoActions.TodoUpdateRequest({
      todoId: todo.id,
      payload: { is_completed: event.checked },
      previousItem: todo,
    }));
  }

  protected startEditing(todo: TodoItem): void {
    if (this.saving()) {
      return;
    }

    this.editingTodoId.set(todo.id);
    this.editingDraft.set(todo.title);
  }

  protected cancelEditing(): void {
    this.editingTodoId.set(null);
    this.editingDraft.set('');
  }

  protected saveEditing(todo: TodoItem): void {
    const title = this.editingDraft().trim();
    if (this.saving()) {
      return;
    }
    if (!title) {
      this.cancelEditing();
      return;
    }
    if (title === todo.title) {
      this.cancelEditing();
      return;
    }

    this.store.dispatch(TodoActions.TodoUpdateRequest({
      todoId: todo.id,
      payload: { title },
      previousItem: todo,
    }));
    this.cancelEditing();
  }

  protected deleteTodo(todo: TodoItem): void {
    this.dialog.open(ConfirmComponent, {
      width: '420px',
      maxWidth: 'calc(100vw - 24px)',
      data: {
        title: 'Delete ToDo',
        message: `Delete "${todo.title}"? This item will be removed from your list.`,
      },
    }).afterClosed().pipe(take(1)).subscribe((confirmed) => {
      if (!confirmed) {
        return;
      }

      this.store.dispatch(TodoActions.TodoDeleteRequest({ todoId: todo.id }));
    });
  }

  protected dropTodo(items: TodoItem[], event: CdkDragDrop<TodoItem[]>): void {
    if (this.saving() || event.previousIndex === event.currentIndex) {
      return;
    }

    const orderedItems = [...items];
    moveItemInArray(orderedItems, event.previousIndex, event.currentIndex);
    const target = orderedItems[event.currentIndex];
    if (!target) {
      return;
    }

    this.store.dispatch(TodoActions.TodoReorderRequest({
      todoId: target.id,
      payload: { target_position: event.currentIndex + 1 },
      previousItems: this.todos(),
    }));
  }

  protected isEditing(todoId: string): boolean {
    return this.editingTodoId() === todoId;
  }

  protected onEditKeydown(event: Event, todo: TodoItem): void {
    const keyboardEvent = event as KeyboardEvent;
    if (keyboardEvent.key === 'Enter' && !keyboardEvent.shiftKey) {
      keyboardEvent.preventDefault();
      this.saveEditing(todo);
      return;
    }
    if (keyboardEvent.key === 'Escape') {
      keyboardEvent.preventDefault();
      this.cancelEditing();
    }
  }

  protected onDraftEnter(event: Event): void {
    const keyboardEvent = event as KeyboardEvent;
    if (keyboardEvent.shiftKey) {
      return;
    }
    keyboardEvent.preventDefault();
    this.addTodo();
  }

  protected trackTodo(_: number, todo: TodoItem): string {
    return todo.id;
  }
}
